import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  CircularProgress,
  Button,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { Print } from '@mui/icons-material';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';

// Number to Words Function
const numberToWords = (num) => {
  if (num === 0) return 'Zero';

  const belowTwenty = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];

  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];
  const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

  const convertChunk = (n) => {
    if (n === 0) return '';
    if (n < 20) return belowTwenty[n];
    if (n < 100) {
      return (
        tens[Math.floor(n / 10)] + (n % 10 ? ' ' + belowTwenty[n % 10] : '')
      );
    }
    return (
      belowTwenty[Math.floor(n / 100)] +
      ' Hundred' +
      (n % 100 ? ' ' + convertChunk(n % 100) : '')
    );
  };

  const [integerPart, decimalPart = ''] = num.toFixed(2).split('.');
  let intPart = parseInt(integerPart);
  const decPart = parseInt(decimalPart);

  if (intPart === 0) return 'Zero Rupees Only';

  let word = '';
  let chunkCount = 0;

  while (intPart > 0) {
    const chunk = intPart % 1000;
    if (chunk) {
      let chunkWord = convertChunk(chunk);
      if (chunkCount > 0) chunkWord += ' ' + thousands[chunkCount];
      word = chunkWord + (word ? ' ' + word : '');
    }
    intPart = Math.floor(intPart / 1000);
    chunkCount++;
  }

  let result = word + ' Rupees';
  if (decPart > 0) {
    result += ' and ' + convertChunk(decPart) + ' Paise';
  }
  result += ' Only';
  return result;
};

const theme = createTheme({
  palette: {
    primary: { main: '#1a1a1a' },
    text: { primary: '#1a1a1a', secondary: '#444' },
    divider: '#d0d0d0',
    background: { paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Helvetica Neue", Arial, sans-serif',
    h6: { fontWeight: 600, fontSize: '0.95rem' },
    body1: { fontSize: '0.75rem' },
    subtitle1: { fontSize: '0.82rem' },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid #eee', py: 0.7, px: 1 },
        head: {
          fontWeight: 600,
          background: '#1e88e5',
          color: '#fff',
          p: '6px 8px',
          fontSize: '0.78rem',
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: '#ccc' } } },
  },
});

export default function RestroInvoice() {
  const { orderId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = sessionStorage.getItem('token');
  const called = React.useRef(false);
  useEffect(() => {
    if (!orderId || called.current) return;

    called.current = true; // 👈 yahin control ho raha hai
    fetchInvoice();
  }, [orderId]);
  const fetchInvoice = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${config.API_BASE_URL}/home/GetRestroinvoice/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // 👇 IMPORTANT: pehle raw text read karo
      const text = await res.text();
      console.log('RAW RESPONSE:', text);

      // 👇 yahan apna generic message mat use karo
      if (!res.ok) {
        throw new Error(text || `Status ${res.status}`);
      }

      const data = JSON.parse(text);
      setInvoice(data);
    } catch (err) {
      console.error('ERROR:', err.message);
      setError(err.message); // 👈 ab yahan real backend error aayega
    } finally {
      setLoading(false);
    }
  };

  const getCurrency = (symbol) => {
    switch (symbol) {
      case '₹':
        return 'Rs.';
      case '$':
        return '$';
      case '€':
        return 'EUR';
      default:
        return symbol || 'Rs.';
    }
  };

  const currency = getCurrency(Companycurrency);

  // ==================== THERMAL PRINTER FUNCTION ====================
  const sendToPrinterAPI = async (order) => {
    const machineId = window.location.hostname;
    const dateTime = new Date().toLocaleString('en-GB');

    const itemsForPrint = order.items || [];

    if (itemsForPrint.length === 0) {
      toast.error('Items not found. Please try again.');
      return;
    }

    const itemsText = itemsForPrint
      .map((item) => {
        const name = (item.name || 'Item').substring(0, 20).padEnd(21, ' ');
        const amount = Number(item.price * (item.qty || 1))
          .toFixed(2)
          .padStart(8, ' ');
        return `${name} ${amount}`;
      })
      .join('\n');

    const totalItems = itemsForPrint.length;
    const divider = '--------------------------------\n';

    // ✅ Company ya Guest check — thermal print ke liye
    const isCompanyBill =
      order.companyAddress && order.companyAddress.trim() !== '';

    // ✅ Bill To section — Guest ya Company
    const billToSection = isCompanyBill
      ? `Bill To  : COMPANY\n` +
        `Name  : ${order.companyname || '-'}\n` +
        `City     : ${order.companyCity || '-'}, ${order.companyState || '-'}\n` +
        `Email    : ${order.companyEmail || '-'}\n` +
        `Mobile   : ${order.companyMobile || '-'}\n` +
        `GST No   : ${order.gstno || '-'}\n`
      : `Bill To  : GUEST\n` +
        `Customer : ${order.title || ''} ${order.firstName || ''} ${order.lastName || ''}\n` +
        `Mobile   : ${order.mobileNumber || '-'}\n` +
        `Address  : ${order.address || '-'}\n` +
        `Email    : ${order.email || '-'}\n`;

    let taxLines = '';

    if (
      itemsForPrint[0]?.isvataplicable === true ||
      itemsForPrint[0]?.isvataplicable === 'true'
    ) {
      taxLines =
        `VAT (${Number(order.vatrate || 0)}%) : ${currency} ${Number(order.vataMount || 0).toFixed(2)}\n` +
        `Discount  : ${order.dicountamount || 0}\n` +
        `Total     : ${currency} ${Number(order.finalPrice || 0).toFixed(2)}\n`;
    } else {
      taxLines =
        `CGST      : ${currency} ${Number(order.cGst || 0).toFixed(2)}\n` +
        `SGST      : ${currency} ${Number(order.sGst || 0).toFixed(2)}\n` +
        `Total GST : ${currency} ${Number(order.gstAmount || 0).toFixed(2)}\n` +
        `Discount  : ${order.dicountamount || 0}\n` +
        `Total     : ${currency} ${Number(order.finalPrice || 0).toFixed(2)}\n`;
    }

    const data =
      '\x1B\x40' +
      '\x1B\x61\x01' +
      (company?.name || 'HOTEL') +
      '\n' +
      (company?.address1 || '') +
      '\n' +
      (company.bankName || '') +
      '\n' +
      (company.bankAccountNumber || '') +
      '\n' +
      (company.ifsc || '') +
      '\n' +
      divider +
      '\x1B\x45\x01' +
      'BILL / INVOICE\n' +
      '\x1B\x45\x00' +
      divider +
      '\x1B\x61\x00' +
      `Order ID : ORD_${order.orderid}\n` +
      `Booking  : ${order.bookingid || '-'}\n` +
      `Date     : ${dateTime}\n` +
      divider +
      // ✅ Dynamic bill to section — company ya guest
      billToSection +
      divider +
      `Items    : ${totalItems}\n` +
      divider +
      'ITEM                AMOUNT\n' +
      divider +
      itemsText +
      '\n' +
      divider +
      `Subtotal : ${currency} ${Number(order.price || 0).toFixed(2)}\n` +
      taxLines +
      divider +
      '\x1B\x61\x01' +
      '***** THANK YOU *****\n' +
      'Please visit again!\n' +
      '\n\n\n\n\n\n' +
      '\x1D\x56\x00';

    try {
      await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'BILL',
          content: data,
          machineId: machineId,
          companyid: company?.id,
          orderId: order.id,
          // ✅ company case mein company city/state, guest mein naam
          customerName: isCompanyBill
            ? `${order.companyCity || ''} ${order.companyState || ''}`
            : `${order.firstName || ''} ${order.lastName || ''}`,
          customerMobile: isCompanyBill
            ? order.companyMobile
            : order.mobileNumber,
          printerName: 'Kitchen',
        }),
      });

      toast.success('Bill sent to small printer successfully!');
    } catch (err) {
      console.error('Print API error:', err);
      toast.error('Failed to send bill to printer');
    }
  };

  const handlePrint = () => {
    if (!invoice) {
      toast.error('Invoice data not loaded yet!');
      return;
    }
    sendToPrinterAPI(invoice);
  };

  if (loading)
    return (
      <Box textAlign='center' mt={10}>
        <CircularProgress size={50} />
      </Box>
    );

  if (error)
    return (
      <Box textAlign='center' mt={10}>
        <Typography color='error'>{error}</Typography>
      </Box>
    );

  if (!invoice)
    return (
      <Box textAlign='center' mt={10}>
        <Typography>No invoice found</Typography>
      </Box>
    );

  const grandTotal = Number(invoice.finalPrice || 0);
  const roundedTotal = Math.round(grandTotal);

  const grandTotalInWords = numberToWords(roundedTotal);
  const totalGst =
    (invoice.cGst || 0) + (invoice.sGst || 0) + (invoice.igst || 0);

  const addressLines = (invoice.address || '').split('\n').filter(Boolean);

  // ✅ companyAddress empty = Guest billing, filled = Company billing
  const isCompanyBilling =
    invoice.companyAddress && invoice.companyAddress.trim() !== '';
  const billToTitle = isCompanyBilling
    ? 'Bill To (Company)'
    : 'Bill To (Guest)';

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f9f9f9', minHeight: '100vh' }}>
        <Box
          className='no-print'
          textAlign='right'
          mb={1.5}
          sx={{ '@media print': { display: 'none' } }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<Print />}
            onClick={() => window.print()}
            sx={{ mr: 1 }}>
            Save PDF / Print A4
          </Button>
          <Button
            variant='contained'
            size='small'
            startIcon={<Print />}
            onClick={handlePrint}>
            Print (Thermal)
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            maxWidth: 900,
            mx: 'auto',
            p: { xs: 2, sm: 2.5, md: 3 },
            border: '1px solid #ddd',
            borderRadius: 1,
            bgcolor: 'white',
            '@media print': {
              p: '12px 16px',
              border: 'none',
              boxShadow: 'none',
              maxWidth: 'none',
            },
          }}>
          {/* Header */}
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='flex-start'
            mb={1.5}>
            <Box>
              {(company.logo || invoice.logo) && (
                <img
                  src={company.logo || invoice.logo}
                  alt='Logo'
                  style={{
                    maxHeight: '55px',
                    marginBottom: '6px',
                    display: 'block',
                  }}
                />
              )}
              <Typography
                variant='h6'
                fontWeight={700}
                gutterBottom
                sx={{ mb: 0.5 }}>
                {invoice.hotelName || 'Restaurant'}
              </Typography>
            </Box>

            <Box textAlign='right'>
              <Box
                sx={{
                  bgcolor: '#1e88e5',
                  color: 'white',
                  px: 2,
                  py: 0.4,
                  borderRadius: 1,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  display: 'inline-block',
                  mb: 0.5,
                }}>
                TAX INVOICE
              </Box>
              <Typography fontSize='0.78rem' mb={0.2}>
                Invoice No: <strong>{invoice.invoiceNumber || '—'}</strong>
              </Typography>
              <Typography fontSize='0.78rem'>
                Date:{' '}
                {invoice.createDate
                  ? new Date(invoice.createDate).toLocaleDateString('en-IN')
                  : new Date().toLocaleDateString('en-IN')}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Issued By + Bill To */}
          <Grid container spacing={3} mb={2.5}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid #eee',
                  borderRadius: 1,
                  minHeight: '140px',
                  bgcolor: '#fafafa',
                }}>
                <Typography
                  variant='subtitle1'
                  fontWeight={600}
                  mb={0.8}
                  color='primary'
                  sx={{ fontSize: '0.9rem' }}>
                  Issued By
                </Typography>
                <Typography fontSize='0.78rem' fontWeight={500} mb={0.6}>
                  {invoice.hotelName}
                </Typography>
                <Typography
                  fontSize='0.75rem'
                  color='text.secondary'
                  lineHeight={1.35}>
                  {invoice.hoteladdress ||
                    'A-31, CIRCUIT HOUSE ROAD, ENGINEERS COLONY'}
                  <br />
                  {invoice.hotelCity || invoice.city || 'Bhilwara'},{' '}
                  {invoice.hotelState || 'Rajasthan'}{' '}
                  {invoice.pincode || '311001'}
                  <br />
                  Mob: {invoice.hotelMobile || '06375775252'}
                  <br />
                  Email: {invoice.hotelEmail || 'info@bisplindia.in'}
                  <br />
                  GSTIN: {invoice.gstno || invoice.gstNumber || '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid #eee',
                  borderRadius: 1,
                  minHeight: '140px',
                  bgcolor: '#fafafa',
                }}>
                <Typography
                  variant='subtitle1'
                  fontWeight={600}
                  mb={0.8}
                  color='primary'
                  sx={{ fontSize: '0.9rem' }}>
                  {billToTitle}
                </Typography>

                {/* ✅ Company billing */}
                {isCompanyBilling ? (
                  <>
                    <Typography fontSize='0.78rem' fontWeight={500} mb={0.6}>
                      {invoice.companyname}
                    </Typography>
                    <Typography
                      fontSize='0.75rem'
                      color='text.secondary'
                      lineHeight={1.35}>
                      {invoice.companyAddress}
                      <br />
                      {invoice.companyCity}, {invoice.companyState}
                      <br />
                      Email: {invoice.companyEmail || '—'}
                      <br />
                      Mobile: {invoice.companyMobile || '—'}
                      <br />
                      GST No: {invoice.gstno || '—'}
                    </Typography>
                  </>
                ) : (
                  /* ✅ Guest billing */
                  <>
                    <Typography fontSize='0.78rem' fontWeight={500} mb={0.6}>
                      {invoice.title} {invoice.firstName} {invoice.lastName}
                    </Typography>
                    <Typography
                      fontSize='0.75rem'
                      color='text.secondary'
                      lineHeight={1.35}>
                      {addressLines.map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                      {invoice.city &&
                        `${invoice.city}, ${invoice.pincode || ''}`}
                      <br />
                      Email: {invoice.email || '—'}
                      <br />
                      Mobile: {invoice.mobileNumber || '—'}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Items Table */}
          <Table size='small' sx={{ mb: 2, minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell width='5%'>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align='right' width='12%'>
                  Price ({Companycurrency})
                </TableCell>
                <TableCell align='right' width='12%'>
                  QTY
                </TableCell>
                <TableCell align='right' width='10%'>
                  GST %
                </TableCell>
                <TableCell align='right' width='12%'>
                  GST Amt ({Companycurrency})
                </TableCell>
                <TableCell align='right' width='12%'>
                  Price ({Companycurrency})
                </TableCell>
                <TableCell align='right' width='12%'>
                  Total Price ({Companycurrency})
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.items?.length > 0 ? (
                invoice.items.map((item, i) => {
                  const gstAmt = ((item.price || 0) * (item.gst || 0)) / 100;
                  return (
                    <TableRow key={i}>
                      <TableCell sx={{ py: 0.6 }}>{i + 1}</TableCell>
                      <TableCell sx={{ py: 0.6 }}>{item.name || '—'}</TableCell>
                      <TableCell align='right' sx={{ py: 0.6 }}>
                        {((item.price || 0) / 1.05).toFixed(2)}
                      </TableCell>
                      <TableCell align='right' sx={{ py: 0.6 }}>
                        {(item.qty || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align='right' sx={{ py: 0.6 }}>
                        {item.gst || 0}%
                      </TableCell>
                      <TableCell align='right' sx={{ py: 0.6 }}>
                        {Number(gstAmt || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ py: 0.6, fontWeight: 500 }}>
                        {(item.price || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ py: 0.6, fontWeight: 500 }}>
                        {((item.price || 0) * (item.qty || 0)).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align='center' sx={{ py: 2 }}>
                    No items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />

          {/* Totals */}
          <Box textAlign='right' mb={2.5}>
            <Grid
              container
              justifyContent='flex-end'
              spacing={1}
              maxWidth={380}
              ml='auto'>
              <Grid item xs={7} textAlign='right'>
                <Typography fontSize='0.76rem'>Base Amount:</Typography>
                <Typography fontSize='0.76rem'>CGST ({2.5}%):</Typography>
                <Typography fontSize='0.76rem'>SGST ({2.5}%):</Typography>
                <Typography fontSize='0.76rem' fontWeight={600}>
                  Total GST:
                </Typography>
                <Divider sx={{ my: 0.8 }} />
                <Typography fontSize='0.98rem' fontWeight={700}>
                  Grand Total:
                </Typography>
              </Grid>
              <Grid item xs={5} textAlign='right'>
                <Typography fontSize='0.76rem'>
                  {Companycurrency} {(invoice.price || 0).toFixed(2)}
                </Typography>
                <Typography fontSize='0.76rem'>
                  {Companycurrency} {(invoice.cGst || 0).toFixed(2)}
                </Typography>
                <Typography fontSize='0.76rem'>
                  {Companycurrency} {(invoice.sGst || 0).toFixed(2)}
                </Typography>
                <Typography fontSize='0.76rem' fontWeight={600}>
                  {Companycurrency} {totalGst.toFixed(2)}
                </Typography>
                <Divider sx={{ my: 0.8 }} />
                <Typography fontSize='1.05rem' fontWeight={700}>
                  {Companycurrency} {Math.round(roundedTotal)}
                </Typography>
              </Grid>
            </Grid>

            <Typography
              mt={1}
              fontSize='0.78rem'
              fontStyle='italic'
              color='text.secondary'>
              <strong>In Words:</strong> {grandTotalInWords}
            </Typography>
          </Box>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2.5}>
            <Typography fontSize='0.60rem'>
              <strong>Bank Name:</strong> {company.bankName}
              <br></br>
              <strong>Account No:</strong> {company.bankAccountNumber}
              <br></br>
              <strong>IFSC:</strong> {company.ifsc}
              <br></br>
              <strong>Bank Address:</strong> {company.bankAddress}
            </Typography>
            {/* #2 Dynamic UPI QR - same source as CreateOrder Scan & Pay */}
            {company.upiId && (
              <Box sx={{ textAlign: 'center', flexShrink: 0, ml: 1 }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&format=png&margin=6&data=${encodeURIComponent(
                    `upi://pay?pa=${encodeURIComponent(company.upiId)}&pn=${encodeURIComponent(
                      invoice.hotelName || company.name || 'Restaurant',
                    )}&am=${encodeURIComponent(Math.round(roundedTotal || 0))}&cu=INR`,
                  )}`}
                  alt='Scan and Pay UPI'
                  width={110}
                  height={110}
                  style={{ display: 'block', margin: '0 auto' }}
                />
                <span style={{ fontSize: '0.6rem', color: '#555' }}>
                  Scan &amp; Pay (UPI)
                </span>
              </Box>
            )}
          </Box>

          {/* Signatures */}
          <Grid container spacing={6} mt={4} justifyContent='space-between'>
            <Grid item xs={5} textAlign='center'>
              <Box height='45px' borderBottom='1px solid #333' mb={0.8} />
              <Typography fontSize='0.82rem' fontWeight={600}>
                Customer Signature
              </Typography>
            </Grid>
            <Grid item xs={5} textAlign='center'>
              <Box height='45px' borderBottom='1px solid #333' mb={0.8} />
              <Typography fontSize='0.82rem' fontWeight={600}>
                Authorized Signature
              </Typography>
              <Typography fontSize='0.75rem' color='text.secondary' mt={0.4}>
                For {invoice.hotelName}
              </Typography>
            </Grid>
          </Grid>

          <Typography
            align='center'
            mt={2.5}
            fontSize='0.72rem'
            color='text.secondary'>
            This is a computer-generated invoice and does not require physical
            signature.
          </Typography>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
